import { CommonErrorModal } from "@app/components/modals/CommonErrorModal";
import {
  CommonModal,
  CommonSelectModal,
} from "@app/components/modals/CommonModal";
import { Canvas } from "@app/components/WidgetStudio/Canvas";
import { Inspector } from "@app/components/WidgetStudio/Inspector";
import { CanvasHeader } from "@app/components/WidgetStudio/Canvas/CanvasHeader";
import { useModal } from "@shortsworks/ui";
import {
  useGetAccountAccessKey,
  useCreateWidget,
  useDeleteWidget,
  useGetWidgets,
  useGetWidget,
  SuccessOrErrorResponse,
  CustomErrorData,
  ErrorCode,
  usePutWidget, WidgetDataSettings, getWidget,
} from "@shortsworks/user-api"
import { useQueryClient } from "@tanstack/react-query";
import {useState, useEffect, useRef} from "react"
import { ShortsWorksWidgetProps } from "@app/components/WidgetStudio/ShortsWorksWidget";
import { useImmer } from "use-immer";
import {WidgetStudioData, WidgetStudioDataType} from "@app/components/WidgetStudio/Inspector/schemas/widgetStudioData";

export const enum DeviceType {
  Desktop = "desktop",
  Mobile = "mobile",
}

interface WidgetStudioProps {
  accessKey: string;
  selectedWidgetId: string;
  // 새로운 저장하기가 완료되었을 경우, 현재 선택된 위젯 정보 변경 필요
  onChangeSelectedWidgetId?: any;
  onDeleteWidget: () => void;
}
export const WidgetStudio = ({
  accessKey,
  selectedWidgetId,
  onDeleteWidget,
}: WidgetStudioProps) => {
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const [widgetStudioData, setWidgetStudioData] = useState<WidgetStudioDataType>(WidgetStudioData);

  const [name, setName] = useState<string | null>(null);
  // const handleChangeName = async (newName: string) => {
  //   console.log(newName);
    // const modalResult = await showModal(CommonSelectModal, {title:"위젯을 변경하시겠습니까?"}).promise();
    // if(modalResult.value) {
    //   return setName(newName);
    // }
  // }
  const [device, setDevice] = useState<DeviceType>(DeviceType.Desktop);
  const [widgetProps, setWidgetProps] = useImmer<ShortsWorksWidgetProps>({
    accessKey,
    widgetProperties: {},
  });

  // 가장 초기 가져오지 못하는 점
  // const { data: widgetData, isSuccess: isSuccessGetWidget } = useGetWidget(selectedWidgetId);
  useEffect(() => {
    // 선택된 위젯 없는 경우
    if(!selectedWidgetId) {
      return setName(null);
    }
    (async () => {
      const getWidgetResult = await getWidget(selectedWidgetId);
      if(getWidgetResult.error) {
        return showModal(CommonErrorModal, {title: "위젯 정보를 불러오는 데 문제가 발생했습니다."});
      }
      setName(getWidgetResult.data.name);
      const modalResult = await showModal(CommonSelectModal, {title:"설정 초기화", description: "위젯이 변경되었습니다. 설정 정보를 불러오겠습니까? 기존에 작업 중인 사항은 초기화됩니다."}).promise();
      if(modalResult.value) {
        setWidgetProps((draft) => {
          // @ts-ignore 타입이 다른 점 체크 필요
          draft.widgetProperties = getWidgetResult.data.settings
        });
        setWidgetStudioData((prev) => {
          return {
            ...prev,
            //@ts-ignore
            ...getWidgetResult.data.settings,
            //@ts-ignore
            // "size": getWidgetResult.data.settings.size,
          }
        })
      }
    })();
  }, [selectedWidgetId]);

  const { queryKey: widgetsQueryKey } = useGetWidgets();
  const { mutateAsync: mutateAsyncCreateWidget } = useCreateWidget({
    mutation: {
      onSuccess: () => {
        showModal(CommonModal, { title: "위젯이 추가되었습니다." });
        return queryClient.invalidateQueries({ queryKey: widgetsQueryKey });
      },
      onError: ({ error }: { error: CustomErrorData }) => {
        if (error?.code === ErrorCode.NUMBER_10004) {
          return showModal(CommonErrorModal, {
            title: "이미 같은 이름의 위젯이 존재합니다.",
          });
        }
        return showModal(CommonErrorModal, {
          title: "위젯 추가하기를 실패했습니다.",
        });
      },
    },
  });

  const { mutateAsync: mutateAsyncUpdateWidget } = usePutWidget({
    mutation: {
      onSuccess: () => {
        showModal(CommonModal, { title: "위젯이 변경되었습니다." });
        return queryClient.invalidateQueries({ queryKey: widgetsQueryKey });
      },
      onError: ({ error }: { error: CustomErrorData }) => {
        if (error?.code === ErrorCode.NUMBER_10004) {
          return showModal(CommonErrorModal, {
            title: "이미 같은 이름의 위젯이 존재합니다.",
          });
        }
        return showModal(CommonErrorModal, {
          title: "위젯 변경하기를 실패했습니다.",
        });
      },
    },
  });

  const handleSaveWidget = async () => {
    if(!name) {
      return showModal(CommonErrorModal, {title: "저장 실패", description: "위젯 이름을 입력해주세요."});
    }

    // 선택된 위젯이 없다면 새로운 위젯
    if(!selectedWidgetId) {
      // 추가된 다음 selectedId를 다시 받아오는 과정 필요
      const modalResult = await showModal(CommonSelectModal, {title: "위젯을 추가하시겠습니까?",}).promise();
      if (!modalResult.value) {
        return;
      }

      return mutateAsyncCreateWidget({
        data: {
          name,
          settings: {
            ...widgetProps?.widgetProperties,
          },
          productIds:
              (widgetProps?.widgetProperties?.productIds as string[]) ?? [],
          groupIds: (widgetProps?.widgetProperties.gruopIds as string[]) ?? [],
          postingIds:
              (widgetProps?.widgetProperties.postingIds as string[]) ?? [],
        },
      });
    }

    const modalResult = await showModal(CommonSelectModal, {title: "변경 사항을 저장하시겠습니까?",}).promise();
    if (!modalResult.value) {
      return;
    }

    // 결과 확인은 mutation에서 작성됨
    return mutateAsyncUpdateWidget({
      widgetId: selectedWidgetId,
      data: {
        name,
        settings: {
          ...widgetProps?.widgetProperties,
        },
        productIds: (widgetProps?.widgetProperties?.productIds as string[]) ?? [],
        groupIds: (widgetProps?.widgetProperties.gruopIds as string[]) ?? [],
        postingIds: (widgetProps?.widgetProperties.postingIds as string[]) ?? [],
      },
    });
  };

  // 추후 상태 관계 처리 필요
  const inspectorRef = useRef<any>();
  const handleResetWidget = async () => {
    const modalResult = await showModal(CommonSelectModal, {title: "변경 사항이 모두 삭제됩니다. 초기화하시겠습니까?"}).promise();
    if(!(modalResult.value && inspectorRef.current)) {
      return showModal(CommonErrorModal, {title: "변경에 실패했습니다."});
    }
      return inspectorRef.current.resetWidgetStudioData();
  }

  const handleDeviceChange = (device: DeviceType) => {
    setDevice(device);
  };

  // 다른 property일 때는 array type으로 전달 필요
  const handleValueChange = (values: Record<string, any> | any) => {
    return setWidgetProps((draft) => {
      draft.widgetProperties = values;
    });
  };

  return (
    <div>
      {/*<WidgetSelector />*/}
      <div className="flex flex-col w-full border rounded-md h-[68vh]">
        <div className="w-full">
          {/* 한번 더 들어가는 현상 체크 */}
          <CanvasHeader
            name={name}
            onChangeName={setName}
            onSaveWidget={handleSaveWidget}
            onResetWidget={handleResetWidget}
            onDeleteWidget={onDeleteWidget}
            device={device}
            onDeviceChange={handleDeviceChange}
          />
        </div>
        <div className="flex flex-row justify-between w-full h-full overflow-hidden">
          <div className="min-w-80 max-w-80 h-full border-r bg-muted/20">
            {/* 선택된 위젯 변경시 default data 변경되도록 props로 전달 */}
            <Inspector ref={inspectorRef} initialWidgetStudioData={widgetStudioData} onValueChange={handleValueChange} />
          </div>
          <div className="w-full h-full">
            <Canvas device={device} widgetProps={widgetProps} />
          </div>
        </div>
      </div>
    </div>
  );
};
